"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface OwnerInfoFormProps {
  onNext: (data: OwnerInfoData) => void;
  onBack: () => void;
}

export interface OwnerInfoData {
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerAddress: string;
  ownerCity: string;
  ownerState: string;
  ownerZip: string;
}

export default function OwnerInfoForm({ onNext, onBack }: OwnerInfoFormProps) {
  const [formData, setFormData] = useState<OwnerInfoData>({
    ownerName: "",
    ownerEmail: "",
    ownerPhone: "",
    ownerAddress: "",
    ownerCity: "",
    ownerState: "",
    ownerZip: "",
  });

  const [errors, setErrors] = useState<Partial<OwnerInfoData>>({});

  const norwegianCounties = [
    "Agder", "Innlandet", "Møre og Romsdal", "Nordland", "Oslo", "Rogaland",
    "Troms og Finnmark", "Trøndelag", "Vestfold og Telemark", "Vestland", "Viken"
  ];

  const handleChange = (field: keyof OwnerInfoData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<OwnerInfoData> = {};

    if (!formData.ownerName) newErrors.ownerName = "Eiernavn er påkrevd";
    if (!formData.ownerEmail) {
      newErrors.ownerEmail = "E-post er påkrevd";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
      newErrors.ownerEmail = "Ugyldig e-postformat";
    }
    if (!formData.ownerPhone) {
      newErrors.ownerPhone = "Telefonnummer er påkrevd";
    } else if (!/^\d{8}$/.test(formData.ownerPhone.replace(/\D/g, ""))) {
      newErrors.ownerPhone = "Telefon må være 8 siffer";
    }
    if (!formData.ownerAddress) newErrors.ownerAddress = "Adresse er påkrevd";
    if (!formData.ownerCity) newErrors.ownerCity = "By er påkrevd";
    if (!formData.ownerState) newErrors.ownerState = "Fylke er påkrevd";
    if (!formData.ownerZip) {
      newErrors.ownerZip = "Postnummer er påkrevd";
    } else if (!/^\d{4}$/.test(formData.ownerZip)) {
      newErrors.ownerZip = "Ugyldig postnummerformat";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-bold text-foreground mb-2">
          Eiers fulle navn *
        </label>
        <input
          type="text"
          value={formData.ownerName}
          onChange={(e) => handleChange("ownerName", e.target.value)}
          placeholder="f.eks. Ola Nordmann"
          className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            errors.ownerName ? "border-error" : "border-neutral-200 hover:border-neutral-300"
          }`}
        />
        {errors.ownerName && (
          <p className="mt-2 text-sm text-error font-medium">{errors.ownerName}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            E-postadresse *
          </label>
          <input
            type="email"
            value={formData.ownerEmail}
            onChange={(e) => handleChange("ownerEmail", e.target.value)}
            placeholder="eier@eksempel.no"
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.ownerEmail ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          />
          {errors.ownerEmail && (
            <p className="mt-2 text-sm text-error font-medium">{errors.ownerEmail}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            Telefonnummer *
          </label>
          <input
            type="tel"
            value={formData.ownerPhone}
            onChange={(e) => handleChange("ownerPhone", e.target.value)}
            placeholder="12345678"
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.ownerPhone ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          />
          {errors.ownerPhone && (
            <p className="mt-2 text-sm text-error font-medium">{errors.ownerPhone}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-foreground mb-2">
          Gateadresse *
        </label>
        <input
          type="text"
          value={formData.ownerAddress}
          onChange={(e) => handleChange("ownerAddress", e.target.value)}
          placeholder="Storgata 1"
          className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            errors.ownerAddress ? "border-error" : "border-neutral-200 hover:border-neutral-300"
          }`}
        />
        {errors.ownerAddress && (
          <p className="mt-2 text-sm text-error font-medium">{errors.ownerAddress}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            By *
          </label>
          <input
            type="text"
            value={formData.ownerCity}
            onChange={(e) => handleChange("ownerCity", e.target.value)}
            placeholder="f.eks. Oslo"
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.ownerCity ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          />
          {errors.ownerCity && (
            <p className="mt-2 text-sm text-error font-medium">{errors.ownerCity}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            Fylke *
          </label>
          <select
            value={formData.ownerState}
            onChange={(e) => handleChange("ownerState", e.target.value)}
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.ownerState ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          >
            <option value="">Velg</option>
            {norwegianCounties.map((county) => (
              <option key={county} value={county}>
                {county}
              </option>
            ))}
          </select>
          {errors.ownerState && (
            <p className="mt-2 text-sm text-error font-medium">{errors.ownerState}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            Postnummer *
          </label>
          <input
            type="text"
            value={formData.ownerZip}
            onChange={(e) => handleChange("ownerZip", e.target.value)}
            placeholder="0123"
            maxLength={4}
            className={`w-full px-5 py-4 bg-white border-2 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.ownerZip ? "border-error" : "border-neutral-200 hover:border-neutral-300"
            }`}
          />
          {errors.ownerZip && (
            <p className="mt-2 text-sm text-error font-medium">{errors.ownerZip}</p>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-8 py-3 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all flex items-center gap-2"
        >
          <Icon name="ArrowLeftIcon" size={18} />
          Tilbake
        </button>
        <button
          type="submit"
          className="px-8 py-3 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-900 transition-all flex items-center gap-2"
        >
          Fortsett til dokumenter
          <Icon name="ArrowRightIcon" size={18} />
        </button>
      </div>
    </form>
  );
}